// src/pages/ELearning/AuditLogPage.tsx
import React, { useEffect, useState } from 'react';
import { getElearningAudit } from '../../api/client';
import {
  Loader2, ClipboardList, User, Calendar, Eye, Check, X,
  FileText, Award, TrendingUp, Filter, Search, ShieldCheck
} from 'lucide-react';

const ACTION_LABELS: Record<string, string> = {
  create: 'Создание',
  read: 'Просмотр',
  update: 'Изменение',
  delete: 'Удаление',
  view: 'Просмотр',
  download: 'Скачивание',
  export: 'Экспорт',
  bulk_update: 'Массовое изменение',
};

const ENTITY_LABELS: Record<string, string> = {
  ElearningTest: 'Тест',
  ElearningAssignment: 'Назначение',
  ElearningResult: 'Результат',
  ElearningBenefit: 'Бенефит',
  ElearningTaskBenefit: 'Задача',
};

const AuditLogPage: React.FC = () => {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterEntity, setFilterEntity] = useState('all');
  const [filterAction, setFilterAction] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params: any = { limit: 200 };
      if (filterEntity !== 'all') params.entity = filterEntity;
      if (filterAction !== 'all') params.action = filterAction;
      const data = await getElearningAudit(params);
      setItems(data.items || []);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filterEntity, filterAction]);

  const filtered = items.filter(i => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (i.userName || '').toLowerCase().includes(q) ||
      (i.entityName || '').toLowerCase().includes(q) ||
      (i.action || '').toLowerCase().includes(q)
    );
  });

  return (
    <>
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .al-hero { animation: fadeUp 0.5s ease-out; }
        .al-row { animation: fadeUp 0.4s ease-out backwards; transition: all 0.25s ease; }
        .al-row:hover { background: rgba(102, 126, 234, 0.06) !important; }
        .al-chip { transition: all 0.25s ease; cursor: pointer; }
        .al-chip:hover { transform: translateY(-2px); }
      `}</style>

      {/* ═══ Hero ═══ */}
      <div className="al-hero" style={styles.hero}>
        <div style={styles.heroIcon}>
          <ClipboardList size={28} color="#fff" />
        </div>
        <div style={{ flex: 1 }}>
          <h1 style={styles.title}>Журнал аудита</h1>
          <p style={styles.subtitle}>Все действия в модуле обучения (ФЗ-152)</p>
        </div>
        {/* VERIFYHR: кнопка верификации сертификата для HR */}
        <a
          href="/certificate/verify"
          target="_blank"
          rel="noopener noreferrer"
          className="al-chip"
          style={styles.verifyLink}
        >
          <ShieldCheck size={16} />
          Проверить сертификат
        </a>
      </div>

      {/* ═══ Filters ═══ */}
      <div style={styles.filtersRow}>
        <div style={styles.searchWrap}>
          <Search size={16} color="#8b8fa8" />
          <input
            type="text"
            placeholder="Поиск по имени/действию/объекту..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={styles.searchInput}
          />
        </div>
        <select value={filterEntity} onChange={e => setFilterEntity(e.target.value)} style={styles.select}>
          <option value="all">Все объекты</option>
          {Object.entries(ENTITY_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select value={filterAction} onChange={e => setFilterAction(e.target.value)} style={styles.select}>
          <option value="all">Все действия</option>
          {Object.entries(ACTION_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {/* ═══ List ═══ */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <Loader2 size={36} className="spin" color="#667eea" style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      ) : error ? (
        <div style={styles.errorCard}>{error}</div>
      ) : filtered.length === 0 ? (
        <div style={styles.empty}>
          <ClipboardList size={48} color="#667eea" style={{ opacity: 0.4 }} />
          <h3 style={{ color: '#5a5f7a', marginTop: 12, fontWeight: 700 }}>Записей нет</h3>
          <p style={{ color: '#8b8fa8', fontSize: 13 }}>Попробуйте другие фильтры</p>
        </div>
      ) : (
        <div style={styles.list}>
          {filtered.map((item, idx) => (
            <div
              key={item._id || idx}
              className="al-row"
              style={{ ...styles.row, animationDelay: `${idx * 0.02}s` }}
            >
              <div style={styles.rowIcon}>
                {item.action === 'create' ? <Check size={16} color="#10b981" /> :
                 item.action === 'delete' ? <X size={16} color="#ef4444" /> :
                 item.action === 'update' ? <TrendingUp size={16} color="#f59e0b" /> :
                 item.action === 'download' ? <Award size={16} color="#667eea" /> :
                 <FileText size={16} color="#8b8fa8" />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.rowMain}>
                  <span style={styles.rowAction}>
                    {ACTION_LABELS[item.action] || item.action}
                  </span>
                  <span style={styles.rowEntity}>
                    {ENTITY_LABELS[item.entity] || item.entity}
                  </span>
                  {item.entityName && (
                    <span style={styles.rowName}>«{item.entityName}»</span>
                  )}
                </div>
                <div style={styles.rowMeta}>
                  {item.userName && (
                    <span style={styles.metaItem}>
                      <User size={12} /> {item.userName}
                    </span>
                  )}
                  <span style={styles.metaItem}>
                    <Calendar size={12} /> {new Date(item.createdAt).toLocaleString('ru-RU')}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  verifyLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 18px',
    background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    color: '#fff',
    borderRadius: 14,
    fontSize: 13,
    fontWeight: 700,
    textDecoration: 'none',
    boxShadow: '0 8px 20px rgba(17, 153, 142, 0.35)',
    flexShrink: 0,
  },
  hero: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 },
  heroIcon: {
    width: 56, height: 56, borderRadius: 18,
    background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 12px 32px rgba(139, 92, 246, 0.4)', flexShrink: 0,
  },
  title: {
    fontSize: 28, fontWeight: 800, margin: 0,
    background: 'linear-gradient(135deg, #1a1a2e 0%, #8b5cf6 100%)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    letterSpacing: '-0.5px',
  },
  subtitle: { color: '#8b8fa8', margin: '4px 0 0', fontSize: 14, fontWeight: 500 },

  filtersRow: { display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' },
  searchWrap: {
    flex: '1 1 240px', display: 'flex', alignItems: 'center', gap: 8,
    background: 'rgba(255,255,255,0.7)',
    border: '1px solid rgba(0,0,0,0.06)',
    borderRadius: 14, padding: '10px 14px',
  },
  searchInput: {
    flex: 1, border: 'none', outline: 'none', background: 'transparent',
    fontSize: 14, fontFamily: 'inherit', color: '#1a1a2e',
  },
  select: {
    padding: '10px 16px', borderRadius: 14,
    border: '1px solid rgba(0,0,0,0.08)',
    background: 'rgba(255,255,255,0.7)',
    fontSize: 13, fontWeight: 600, color: '#5a5f7a',
    cursor: 'pointer', fontFamily: 'inherit',
  },

  list: { display: 'flex', flexDirection: 'column', gap: 6 },
  row: {
    display: 'flex', alignItems: 'center', gap: 14,
    padding: '14px 16px',
    background: 'rgba(255, 255, 255, 0.55)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    borderRadius: 16,
    border: '1px solid rgba(255, 255, 255, 0.6)',
    boxShadow: '0 2px 10px rgba(31, 38, 135, 0.04)',
  },
  rowIcon: {
    width: 36, height: 36, borderRadius: 10,
    background: 'rgba(102, 126, 234, 0.1)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  rowMain: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 },
  rowAction: {
    fontSize: 13, fontWeight: 700, color: '#1a1a2e',
  },
  rowEntity: {
    padding: '2px 8px', background: 'rgba(102, 126, 234, 0.12)',
    color: '#667eea', borderRadius: 6, fontSize: 11, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: 0.3,
  },
  rowName: {
    fontSize: 13, color: '#5a5f7a', fontWeight: 500,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  rowMeta: { display: 'flex', gap: 14, flexWrap: 'wrap' },
  metaItem: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    fontSize: 11, color: '#8b8fa8', fontWeight: 500,
  },

  empty: {
    textAlign: 'center', padding: 60,
    background: 'rgba(255,255,255,0.5)',
    backdropFilter: 'blur(20px)',
    borderRadius: 24, border: '1px dashed rgba(102,126,234,0.3)',
  },
  errorCard: {
    padding: 20, background: 'rgba(239, 68, 68, 0.1)',
    borderRadius: 12, color: '#ef4444', fontSize: 14, fontWeight: 600,
  },
};

export default AuditLogPage;
